/**
 * Interaction Timing Instrumentation
 *
 * This module provides comprehensive timing measurements for:
 * 1. Element Inspector value changes → repaint completion
 * 2. Drag and drop operations
 * 3. Canvas repaints and renders
 *
 * Integration points:
 * - ElementInspector.vue: updateModelValue method
 * - DynamicComponentElementStaging.vue: watch handlers and calculateOptionValues
 * - drag-and-drop-mixin-staging.js: drag handlers
 * - StagingFrameRightPanel.vue: updateOptions/updateBrandingConfig methods
 * - DynamicComponentInfiniteConfigBuilder.vue: border update scheduling
 */

import { PerformanceTimer } from "@vcl";

/**
 * Timing categories for performance grouping
 */
export const TimingGroups = {
  INSPECTOR_INTERACTION: "inspector-interaction",
  DRAG_AND_DROP: "drag-and-drop",
  REPAINT_CYCLE: "repaint-cycle",
  OPTION_CALCULATION: "option-calculation",
};

/**
 * Tracks the complete lifecycle of an inspector option change
 * from user input to visual repaint completion
 */
export class InspectorChangeTracker {
  constructor() {
    this.activeTimers = new Map();
    this.completedMeasurements = [];
  }

  /**
   * Start tracking an inspector option change
   * Call this in ElementInspector.updateModelValue before emitting
   *
   * @param {string} optionKey - The option being changed (e.g., "default.font-size")
   * @param {any} oldValue - Previous value
   * @param {any} newValue - New value
   * @param {string} elementIdentifier - Element being modified
   * @returns {string} timerId - Use this to track the change lifecycle
   */
  // Add timeout fallback to inspector tracker
  startInspectorChange(optionKey, oldValue, newValue, elementIdentifier) {
    const changeId = `${elementIdentifier}-${optionKey}-${Date.now()}`;

    const parentTimer = PerformanceTimer.startTimer(
      `Inspector Change: ${optionKey}`,
      TimingGroups.INSPECTOR_INTERACTION,
    );

    const phaseTimers = {
      parent: parentTimer,
      emit: null,
      optionCalculation: null,
      repaint: null,
      complete: false,
    };

    this.activeTimers.set(changeId, {
      optionKey,
      oldValue,
      newValue,
      elementIdentifier,
      startTime: Date.now(),
      timers: phaseTimers,
    });

    setTimeout(() => {
      const tracking = this.activeTimers.get(changeId);
      if (tracking && !tracking.timers.complete) {
        this.forceCompleteChange(changeId);
      }
    }, 5000);

    return changeId;
  }

  forceCompleteChange(changeId) {
    const tracking = this.activeTimers.get(changeId);
    if (!tracking) {
      return;
    }

    // Stop all timers
    if (tracking.timers.parent) {
      PerformanceTimer.stopTimer(tracking.timers.parent);
    }
    if (tracking.timers.emit) {
      PerformanceTimer.stopTimer(tracking.timers.emit);
    }
    if (tracking.timers.optionCalculation) {
      PerformanceTimer.stopTimer(tracking.timers.optionCalculation);
    }
    if (tracking.timers.repaint) {
      PerformanceTimer.stopTimer(tracking.timers.repaint);
    }

    tracking.timers.complete = true;
    this.activeTimers.delete(changeId);
  }

  /**
   * Mark when the value has been emitted to the store
   * Call this after this.$emit("update:before-model-value") completes
   */
  markEmitComplete(changeId) {
    const tracking = this.activeTimers.get(changeId);
    if (!tracking) {
      return;
    }

    tracking.timers.emit = PerformanceTimer.startTimer(
      "Emit to Store",
      TimingGroups.INSPECTOR_INTERACTION,
    );

    // Emit is immediate, stop right away
    PerformanceTimer.stopTimer(tracking.timers.emit);
  }

  /**
   * Start tracking option value calculation phase
   * Call this at the start of DynamicComponentElementStaging.calculateOptionValues
   */
  startOptionCalculation(elementIdentifier) {
    // Find active changes for this element
    // eslint-disable-next-line
    for (const [changeId, tracking] of this.activeTimers.entries()) {
      if (tracking.elementIdentifier === elementIdentifier && !tracking.timers.optionCalculation) {
        tracking.timers.optionCalculation = PerformanceTimer.startTimer(
          `Calculate Options: ${tracking.optionKey}`,
          TimingGroups.OPTION_CALCULATION,
        );
        break;
      }
    }
  }

  /**
   * Mark option calculation complete
   * Call this in the calculateOptionValues completion callback
   */
  completeOptionCalculation(elementIdentifier) {
    for (const [changeId, tracking] of this.activeTimers.entries()) {
      if (tracking.elementIdentifier === elementIdentifier && tracking.timers.optionCalculation) {
        PerformanceTimer.stopTimer(tracking.timers.optionCalculation);

        // Start repaint tracking
        tracking.timers.repaint = PerformanceTimer.startTimer(
          `Repaint: ${tracking.optionKey}`,
          TimingGroups.REPAINT_CYCLE,
        );

        // Schedule repaint completion check
        this.scheduleRepaintCheck(changeId);
        break;
      }
    }
  }

  /**
   * Schedule a check for repaint completion
   * Uses requestAnimationFrame to detect when the browser has painted
   */
  scheduleRepaintCheck(changeId) {
    const tracking = this.activeTimers.get(changeId);
    if (!tracking) {
      return;
    }

    // Wait for next animation frame (browser has committed the change)
    requestAnimationFrame(() => {
      // Wait one more frame to ensure paint is complete
      requestAnimationFrame(() => {
        this.completeRepaint(changeId);
      });
    });
  }

  /**
   * Mark repaint as complete
   */
  completeRepaint(changeId) {
    const tracking = this.activeTimers.get(changeId);
    if (!tracking || !tracking.timers.repaint) {
      return;
    }

    PerformanceTimer.stopTimer(tracking.timers.repaint);
    PerformanceTimer.stopTimer(tracking.timers.parent);

    tracking.timers.complete = true;
    tracking.endTime = Date.now();
    tracking.totalDuration = tracking.endTime - tracking.startTime;

    // Move to completed measurements
    this.completedMeasurements.push({
      ...tracking,
      completedAt: new Date().toISOString(),
    });

    this.activeTimers.delete(changeId);
  }

  /**
   * Get summary of completed measurements
   */
  getSummary() {
    return {
      active: Array.from(this.activeTimers.values()).map(t => ({
        optionKey: t.optionKey,
        elementIdentifier: t.elementIdentifier,
        elapsed: Date.now() - t.startTime,
      })),
      completed: this.completedMeasurements.slice(-20).map(m => ({
        optionKey: m.optionKey,
        elementIdentifier: m.elementIdentifier,
        totalDuration: m.totalDuration,
        completedAt: m.completedAt,
      })),
    };
  }

  /**
   * Clear all tracking data
   */
  reset() {
    this.activeTimers.clear();
    this.completedMeasurements = [];
  }
}

/**
 * Tracks drag and drop operation timing
 */
export class DragDropTracker {
  constructor() {
    this.activeOperation = null;
    this.completedOperations = [];
  }

  /**
   * Start tracking a drag operation
   * Call this in internalHandleDragStart
   *
   * @param {string} identifier - Element identifier being dragged
   * @param {boolean} isMultiDrag - Whether this is a multi-element drag
   */
  startDrag(identifier, isMultiDrag = false) {
    const parentTimer = PerformanceTimer.startTimer(
      isMultiDrag ? "Multi-Element Drag" : "Single Element Drag",
      TimingGroups.DRAG_AND_DROP,
    );

    this.activeOperation = {
      identifier,
      isMultiDrag,
      startTime: Date.now(),
      timers: {
        parent: parentTimer,
        dragImageGeneration: null,
        dragOver: [],
        drop: null,
        repaint: null,
      },
      dragOverCount: 0,
    };
  }

  /**
   * Track drag image generation time
   * Call this after generateDragImageAndMousePosition completes
   */
  markDragImageGenerated() {
    if (!this.activeOperation) {
      return;
    }

    this.activeOperation.timers.dragImageGeneration = PerformanceTimer.startTimer(
      "Generate Drag Image",
      TimingGroups.DRAG_AND_DROP,
    );
    PerformanceTimer.stopTimer(this.activeOperation.timers.dragImageGeneration);
  }

  /**
   * Track each dragOver event
   * Call this in internalHandleDragOver
   */
  recordDragOver(containerIdentifier) {
    if (!this.activeOperation) {
      return;
    }

    const dragOverTimer = PerformanceTimer.startTimer(
      `Drag Over ${containerIdentifier}`,
      TimingGroups.DRAG_AND_DROP,
    );

    PerformanceTimer.stopTimer(dragOverTimer);

    this.activeOperation.timers.dragOver.push({
      container: containerIdentifier,
      timer: dragOverTimer,
      timestamp: Date.now(),
    });
    this.activeOperation.dragOverCount++;
  }

  /**
   * Track drop operation
   * Call this at the start of handleDrop
   */
  startDrop(containerIdentifier) {
    if (!this.activeOperation) {
      return;
    }

    this.activeOperation.dropContainer = containerIdentifier;
    this.activeOperation.timers.drop = PerformanceTimer.startTimer(
      `Drop at ${containerIdentifier}`,
      TimingGroups.DRAG_AND_DROP,
    );
  }

  /**
   * Complete drop operation and start repaint tracking
   * Call this after drop logic completes but before repaint
   */
  completeDrop() {
    if (!this.activeOperation || !this.activeOperation.timers.drop) {
      return;
    }

    PerformanceTimer.stopTimer(this.activeOperation.timers.drop);
  }

  /**
   * Schedule repaint completion check
   */
  scheduleRepaintCheck() {
    if (!this.activeOperation) {
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.completeRepaint();
      });
    });
  }

  /**
   * Complete the repaint phase
   */
  completeRepaint() {
    if (!this.activeOperation) {
      return;
    }

    if (this.activeOperation.timers.repaint) {
      PerformanceTimer.stopTimer(this.activeOperation.timers.repaint);
    }

    PerformanceTimer.stopTimer(this.activeOperation.timers.parent);

    this.activeOperation.endTime = Date.now();
    this.activeOperation.totalDuration = this.activeOperation.endTime - this.activeOperation.startTime;
    this.activeOperation.completedAt = new Date().toISOString();

    this.completedOperations.push(this.activeOperation);
    this.activeOperation = null;
  }

  /**
   * Cancel active drag operation (e.g., on dragEnd without drop)
   */
  cancelDrag() {
    if (!this.activeOperation) {
      return;
    }

    PerformanceTimer.stopTimer(this.activeOperation.timers.parent);
    this.activeOperation = null;
  }

  /**
   * Get summary of drag operations
   */
  getSummary() {
    const summary = {
      active: this.activeOperation ? {
        identifier: this.activeOperation.identifier,
        isMultiDrag: this.activeOperation.isMultiDrag,
        dragOverCount: this.activeOperation.dragOverCount,
        elapsed: Date.now() - this.activeOperation.startTime,
      } : null,
      completed: this.completedOperations.slice(-10).map(op => ({
        identifier: op.identifier,
        isMultiDrag: op.isMultiDrag,
        dropContainer: op.dropContainer,
        dragOverCount: op.dragOverCount,
        totalDuration: op.totalDuration,
        completedAt: op.completedAt,
      })),
    };

    return summary;
  }

  /**
   * Get detailed statistics
   */
  getStatistics() {
    if (this.completedOperations.length === 0) {
      return null;
    }

    const durations = this.completedOperations.map(op => op.totalDuration);
    const dragOverCounts = this.completedOperations.map(op => op.dragOverCount);

    return {
      totalOperations: this.completedOperations.length,
      averageDuration: durations.reduce((a, b) => a + b, 0) / durations.length,
      minDuration: Math.min(...durations),
      maxDuration: Math.max(...durations),
      averageDragOvers: dragOverCounts.reduce((a, b) => a + b, 0) / dragOverCounts.length,
    };
  }

  /**
   * Reset all tracking data
   */
  reset() {
    if (this.activeOperation?.timers?.parent) {
      PerformanceTimer.stopTimer(this.activeOperation.timers.parent);
    }
    this.activeOperation = null;
    this.completedOperations = [];
  }
}

/**
 * Tracks canvas repaint operations
 */
export class CanvasRepaintTracker {
  constructor() {
    this.activeRepaint = null;
    this.completedRepaints = [];
  }

  /**
   * Start tracking a canvas repaint
   * Call this when scheduleBorderUpdate is triggered
   */
  startRepaint(triggerReason = "unknown") {
    // If there's already an active repaint, complete it first
    if (this.activeRepaint) {
      this.completeRepaint();
    }

    const timer = PerformanceTimer.startTimer(
      `Canvas Repaint: ${triggerReason}`,
      TimingGroups.REPAINT_CYCLE,
    );

    this.activeRepaint = {
      triggerReason,
      startTime: Date.now(),
      timer,
      rafCompleted: false,
    };
  }

  /**
   * Mark when the first RAF completes
   */
  markFirstRAF() {
    if (!this.activeRepaint) {
      return;
    }
    this.activeRepaint.firstRAFTime = Date.now();
  }

  /**
   * Mark when the second RAF completes (actual paint)
   */
  markSecondRAF() {
    if (!this.activeRepaint) {
      return;
    }
    this.activeRepaint.secondRAFTime = Date.now();
    this.activeRepaint.rafCompleted = true;
  }

  /**
   * Complete the repaint tracking
   */
  completeRepaint() {
    if (!this.activeRepaint) {
      return;
    }

    PerformanceTimer.stopTimer(this.activeRepaint.timer);

    this.activeRepaint.endTime = Date.now();
    this.activeRepaint.totalDuration = this.activeRepaint.endTime - this.activeRepaint.startTime;
    this.activeRepaint.completedAt = new Date().toISOString();

    this.completedRepaints.push(this.activeRepaint);

    // Keep only last 50 repaints
    if (this.completedRepaints.length > 50) {
      this.completedRepaints.shift();
    }

    this.activeRepaint = null;
  }

  /**
   * Reset tracking
   */
  reset() {
    if (this.activeRepaint?.timer) {
      PerformanceTimer.stopTimer(this.activeRepaint.timer);
    }
    this.activeRepaint = null;
    this.completedRepaints = [];
  }
}

/**
 * Global singleton instances
 */
export const inspectorTracker = new InspectorChangeTracker();
export const dragDropTracker = new DragDropTracker();
export const canvasRepaintTracker = new CanvasRepaintTracker();

/**
 * Reset all tracking
 */
export function resetAllTracking() {
  inspectorTracker.reset();
  dragDropTracker.reset();
  canvasRepaintTracker.reset();
}
